import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Modal from "@/components/Modal";
import { notify, fmtError } from "@/lib/notify";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Users, Plus, X, Loader2, AlertTriangle, CheckCircle2, Shield, Trash2, ToggleLeft, ToggleRight,
  Building2, Edit2, Check, UserCheck, Sparkles, ArrowLeft,
} from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Erreur.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const { user: me } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState("users");
  const isSuperAdmin = !!me?.is_super_admin;
  const tabs = isSuperAdmin
    ? [{ k: "users", label: "Utilisateurs", icon: Users }, { k: "orgs", label: "Organisations", icon: Building2 }]
    : [{ k: "users", label: "Utilisateurs", icon: Users }];

  return (
    <div className="space-y-6" data-testid="admin-root">
      <Link
        to="/app/executive"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-100 transition-colors"
        data-testid="admin-back-to-cockpit"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("nav.back_to_cockpit", { defaultValue: "Retour à l'environnement" })}
      </Link>
      <div>
        <div className="flex items-center gap-2 text-[#378ADD] noam-overline">
          <Shield className="w-3 h-3" /> Administration
        </div>
        <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">
          {tab === "users" ? "Gestion des comptes" : "Gestion des organisations"}
        </h1>
        <p className="text-zinc-400 text-sm mt-1">
          {tab === "users"
            ? "Créer, désactiver et supprimer les utilisateurs de la plateforme."
            : "Créer plusieurs organisations, modifier nom / secteur / phase / statut."}
        </p>
      </div>

      <div className="flex gap-1 border-b border-white/5" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors ${
              tab === t.k ? "border-[#378ADD] text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"
            }`}
            data-testid={`admin-tab-${t.k}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersTab me={me} />}
      {tab === "orgs" && isSuperAdmin && <OrgsTab />}
    </div>
  );
}

function UsersTab({ me }) {
  const [users, setUsers] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      setError(formatErr(e?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); await load(); }
    catch (e) { setError(formatErr(e?.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte ${u.email} ?`)) return;
    try { await api.delete(`/users/${u.id}`); await load(); }
    catch (e) { setError(formatErr(e?.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end">
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs" data-testid="admin-create-btn">
          <Plus className="w-3.5 h-3.5" /> Créer un utilisateur
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="admin-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
          <span>{error}</span>
          <button className="ml-auto text-[#FCA5A5]" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      <div className="noam-card p-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
          <div className="col-span-4">Email</div>
          <div className="col-span-3">Nom · Organisation</div>
          <div className="col-span-1">Rôle</div>
          <div className="col-span-2">Créé le</div>
          <div className="col-span-1 text-center">Statut</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {users === null && (
          <div className="px-5 py-6 text-zinc-400 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}
        {users && users.length === 0 && (
          <div className="px-5 py-10 text-zinc-500 text-sm text-center italic">
            Aucun utilisateur — créez le premier compte.
          </div>
        )}
        {users && users.map((u) => {
          const isMe = u.id === me?.id;
          return (
            <div
              key={u.id}
              className="grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-white/5 text-sm hover:bg-white/[0.02]"
              data-testid={`admin-user-row-${u.id}`}
            >
              <div className="col-span-4 truncate">
                <span className="text-white">{u.email}</span>
                {isMe && <span className="ml-2 text-[10px] text-[#378ADD]">(vous)</span>}
              </div>
              <div className="col-span-3 text-zinc-400 truncate">
                {u.name || "—"}{u.organisation ? ` · ${u.organisation}` : ""}
              </div>
              <div className="col-span-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                  u.role === "admin"
                    ? "border-[#378ADD]/40 bg-[#378ADD]/10 text-[#B5D4F4]"
                    : "border-white/10 text-zinc-300"
                }`}>
                  {u.role}
                </span>
              </div>
              <div className="col-span-2 text-xs text-zinc-500">
                {new Date(u.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
              </div>
              <div className="col-span-1 text-center">
                {u.active ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#1D9E75]"><CheckCircle2 className="w-3 h-3" /> actif</span>
                ) : (
                  <span className="text-[10px] text-zinc-500">désactivé</span>
                )}
              </div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <button
                  onClick={() => toggleActive(u)}
                  disabled={isMe && u.active}
                  className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  title={u.active ? "Désactiver" : "Activer"}
                  data-testid={`admin-toggle-${u.id}`}
                >
                  {u.active ? <ToggleRight className="w-4 h-4 text-[#1D9E75]" /> : <ToggleLeft className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => remove(u)}
                  disabled={isMe}
                  className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#E24B4A] disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Supprimer"
                  data-testid={`admin-delete-${u.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
        />
      )}
    </>
  );
}

function OrgsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/workspace/organisations");
      setData(r.data);
    } catch (e) { setError(formatErr(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (o) => {
    setEditingId(o.id);
    setDraft({ name: o.name || "", sector: o.sector || "", phase: o.phase || "Stabilisation", status: o.status || "active" });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/workspace/admin/organisations/${editingId}`, draft);
      setEditingId(null);
      await load();
    } catch (e) { setError(formatErr(e?.response?.data?.detail)); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Supprimer l'organisation "${o.name}" et toutes ses données (risques, audits…) ?`)) return;
    try { await api.delete(`/workspace/admin/organisations/${o.id}`); await load(); }
    catch (e) { setError(formatErr(e?.response?.data?.detail)); }
  };
  const impersonate = async (o) => {
    if (!window.confirm(`Passer dans le workspace "${o.name}" (impersonation) ? Vous verrez les données de ce client.`)) return;
    try {
      await api.post(`/workspace/admin/organisations/${o.id}/impersonate`);
      // Reload entire app so all data refetches against the new active org
      window.location.href = "/app/executive";
    } catch (e) { setError(formatErr(e?.response?.data?.detail)); }
  };
  const seedDemo = async () => {
    if (!window.confirm("Peupler l'organisation active avec un jeu de données réaliste (secteur financier suisse) ? Idempotent : aucune duplication si déjà rempli.")) return;
    try {
      const r = await api.post("/workspace/admin/seed-demo-data");
      const ins = r.data?.inserted || {};
      const summary = Object.keys(ins).length === 0
        ? "Aucune insertion : toutes les collections étaient déjà peuplées."
        : Object.entries(ins).map(([k, v]) => `${k}: ${v}`).join(" · ");
      notify.success(`Données générées dans « ${r.data?.organisation_name} »`, { description: summary });
    } catch (e) { setError(formatErr(e?.response?.data?.detail)); }
  };

  const STATUS_META = {
    active: { label: "Actif", color: "#1D9E75" },
    suspended: { label: "Suspendu", color: "#EF9F27" },
    archived: { label: "Archivé", color: "#A1A1AA" },
  };

  return (
    <>
      <div className="flex justify-end gap-2">
        <button onClick={seedDemo} className="text-xs px-3 py-1.5 rounded-md border border-[#7F77DD]/40 text-[#B5D4F4] hover:bg-[#7F77DD]/10 inline-flex items-center gap-1.5" data-testid="admin-seed-demo-btn">
          <Sparkles className="w-3.5 h-3.5" /> Peupler données démo (org active)
        </button>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs" data-testid="admin-org-create-btn">
          <Plus className="w-3.5 h-3.5" /> Créer une organisation
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> <span>{error}</span>
          <button className="ml-auto text-[#FCA5A5]" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      <div className="noam-card p-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
          <div className="col-span-3">Nom</div>
          <div className="col-span-2">Secteur</div>
          <div className="col-span-2">Phase</div>
          <div className="col-span-2 text-center">Statut</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {!data && (
          <div className="px-5 py-6 text-zinc-400 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}
        {data && (data.organisations || []).length === 0 && (
          <div className="px-5 py-10 text-zinc-500 text-sm text-center italic">Aucune organisation.</div>
        )}
        {data && (data.organisations || []).map((o) => {
          const editing = editingId === o.id;
          const sm = STATUS_META[o.status || "active"] || STATUS_META.active;
          return (
            <div key={o.id} className="grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-white/5 text-sm hover:bg-white/[0.02]" data-testid={`admin-org-row-${o.slug}`}>
              <div className="col-span-3">
                {editing ? (
                  <input
                    autoFocus
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="noam-input text-sm"
                    data-testid={`admin-org-name-input-${o.slug}`}
                  />
                ) : (
                  <span className="text-white">{o.name}</span>
                )}
                <div className="text-[10px] text-zinc-600 mt-0.5">{o.slug}</div>
              </div>
              <div className="col-span-2">
                {editing ? (
                  <select value={draft.sector || ""} onChange={(e) => setDraft({ ...draft, sector: e.target.value })} className="noam-input text-xs">
                    <option value="">—</option>
                    {["Banque", "Assurance", "Santé", "Industrie", "PME", "Autre"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                ) : (
                  <span className="text-zinc-400 text-xs">{o.sector || "—"}</span>
                )}
              </div>
              <div className="col-span-2">
                {editing ? (
                  <select value={draft.phase} onChange={(e) => setDraft({ ...draft, phase: e.target.value })} className="noam-input text-xs">
                    {["Stabilisation", "Déploiement", "Restructuration"].map((p) => <option key={p}>{p}</option>)}
                  </select>
                ) : (
                  <span className="text-[#378ADD] text-xs">{o.phase || "Stabilisation"}</span>
                )}
              </div>
              <div className="col-span-2 text-center">
                {editing ? (
                  <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="noam-input text-xs">
                    {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${sm.color}1A`, color: sm.color, border: `1px solid ${sm.color}55` }}>
                    {sm.label}
                  </span>
                )}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-1">
                {editing ? (
                  <>
                    <button onClick={saveEdit} className="text-[#1D9E75] p-1.5 rounded hover:bg-white/5" title="Enregistrer" data-testid={`admin-org-save-${o.slug}`}>
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-zinc-500 p-1.5 rounded hover:bg-white/5" title="Annuler">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => impersonate(o)} className="text-zinc-400 hover:text-[#378ADD] p-1.5 rounded hover:bg-white/5" title="Entrer dans ce workspace (impersonation)" data-testid={`admin-org-impersonate-${o.slug}`}>
                      <UserCheck className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => startEdit(o)} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" title="Modifier" data-testid={`admin-org-edit-${o.slug}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => remove(o)} className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5" title="Supprimer" data-testid={`admin-org-delete-${o.slug}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <CreateOrgModalAdmin
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await load(); }}
        />
      )}
    </>
  );
}

function CreateOrgModalAdmin({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", sector: "", phase: "Stabilisation", status: "active",
    admin_email: "", admin_password: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      // If admin email provided, use the with-admin endpoint that also creates the user
      if (form.admin_email.trim()) {
        await api.post("/workspace/admin/organisations/with-admin", {
          name: form.name.trim(),
          sector: form.sector || null,
          phase: form.phase,
          status: form.status,
          admin_email: form.admin_email.trim(),
          admin_password: form.admin_password.trim() || undefined,
        });
      } else {
        await api.post("/workspace/organisations", {
          name: form.name.trim(),
          sector: form.sector || null,
        });
      }
      onCreated();
    } catch (err) { setError(formatErr(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-2xl" testid="admin-org-create-modal">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="noam-overline">Nouvelle organisation</div>
            <div className="noam-display text-lg font-semibold mt-1">Créer un environnement client</div>
            <p className="text-[11px] text-zinc-500 mt-1.5">Renseignez le nom de l'organisation. Optionnellement, créez l'admin client en même temps.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {/* Section identité */}
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Identité</div>
            <div>
              <label className="text-[11px] text-zinc-400 mb-1 block">Nom de l'organisation <span className="text-[#E24B4A]">*</span></label>
              <input
                autoFocus required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex : Banque Privée Lémanique"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                data-testid="admin-org-create-name"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[11px] text-zinc-400 mb-1 block">Secteur</label>
                <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                  data-testid="admin-org-create-sector">
                  <option value="">—</option>
                  {["Banque", "Assurance", "Santé", "Industrie", "PME", "Autre"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 mb-1 block">Phase</label>
                <select value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                  data-testid="admin-org-create-phase">
                  {["Stabilisation", "Déploiement", "Restructuration"].map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 mb-1 block">Statut</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                  data-testid="admin-org-create-status">
                  <option value="active">Actif</option>
                  <option value="suspended">Suspendu</option>
                  <option value="archived">Archivé</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section admin client (optional) */}
          <div className="space-y-3 pt-3 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Admin client (facultatif)</div>
            <p className="text-[11px] text-zinc-500 -mt-2">Si vous renseignez un email, un compte Admin sera créé automatiquement avec un mot de passe temporaire.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-zinc-400 mb-1 block">Email admin</label>
                <input
                  type="email"
                  value={form.admin_email}
                  onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                  placeholder="admin@client.ch"
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                  data-testid="admin-org-create-admin-email"
                />
              </div>
              <div>
                <label className="text-[11px] text-zinc-400 mb-1 block">Mot de passe temporaire</label>
                <input
                  value={form.admin_password}
                  onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                  placeholder="auto-généré si vide"
                  className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
                  data-testid="admin-org-create-admin-password"
                />
              </div>
            </div>
          </div>

          {error && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{error}</div>}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="admin-org-create-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Création…</> : <>Créer l'organisation</>}
          </button>
        </form>
    </Modal>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "", organisation: "", role: "user" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await api.post("/users", form);
      onCreated();
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" testid="admin-create-modal">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#378ADD]" />
            <div>
              <div className="noam-overline">Nouvel utilisateur</div>
              <div className="noam-display text-base font-semibold mt-0.5">Créer un compte</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={submit} className="space-y-3.5">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="noam-input" autoFocus data-testid="admin-form-email" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Mot de passe (8+ caractères)</label>
            <input type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="noam-input font-mono" data-testid="admin-form-password" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom (facultatif)</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="noam-input" data-testid="admin-form-name" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Rôle</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="noam-input" data-testid="admin-form-role">
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Organisation (facultatif)</label>
            <input value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} className="noam-input" data-testid="admin-form-org" />
          </div>

          {error && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs" data-testid="admin-form-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="admin-form-submit">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Création…</> : <>Créer le compte</>}
          </button>
        </form>
    </Modal>
  );
}
